// Hooks
import { useRouter } from 'next/router';
import React, { useEffect, useState, createRef } from 'react';

// Contentful
import safeJsonStringify from 'safe-json-stringify';
import resolveResponse from 'contentful-resolve-response';
import contentfulClient from '../../client/contentful';

// Components
import CallToAction from '../../components/CallToAction/CallToAction';
import Footer from '../../components/Footer/Footer';
import Navigation from '../../components/Navigation/Navigation';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import PlaybookHeader from '../../components/PlaybookHeader/PlaybookHeader';
import PlaybookLayout from '../../components/PlaybookLayout/PlaybookLayout';
import SummaryBlock from '../../components/SummaryBlock/SummaryBlock';
import ImpactBlock from '../../components/ImpactBlock/ImpactBlock';
import SubscribeBlock from '../../components/SubscribeBlock/SubscribeBlock';
import SideMenu from '../../components/SideMenu/SideMenu';
import Chapter from '../../components/Chapter/Chapter';
import SeoMeta from '../../components/SeoMeta/SeoMeta';

// Data Utils
import getSeoData from '../../utils/data/getSeoData';
import getNavigationData from '../../utils/data/getNavigationData';
import getPlaybookHeaderData from '../../utils/data/getPlaybookHeaderData';
import getSummaryBlockData from '../../utils/data/getSummaryBlockData';
import getChaptersBlockData from '../../utils/data/getChaptersBlockData';
import getImpactBlockData from '../../utils/data/getImpactBlockData';
import getSidemenuData from '../../utils/data/getSidemenuData';
import getSubscribeBlockData from '../../utils/data/getSubscribeBlockData';
import getCallToActionData from '../../utils/data/getCallToActionData';
import useOpenGraphImage from '../../utils/useOpenGraphImage';

const Playbook = (props) => {
  const data = resolveResponse(props.playbook)[0];

  const { seoImageURL } = useOpenGraphImage(`/playbooks-${data.fields.slug}`);

  const router = useRouter();

  const seoData = getSeoData(data.fields, seoImageURL);

  const sidemenuData = getSidemenuData(data);
  const navigationData = getNavigationData(data);
  const playbookHeaderData = getPlaybookHeaderData(data);
  const summaryBlockData = getSummaryBlockData(data);
  const chaptersBlockData = getChaptersBlockData(data);
  const impactBlockData = getImpactBlockData(data);
  const subscribeBlockData = getSubscribeBlockData(data);

  const callToActionData = data?.fields?.contextualizedCta
    ? getCallToActionData(data?.fields?.contextualizedCta)
    : false;

  const [scrollMargin, setScrollMargin] = useState();
  const [activeAnchor, setActiveAnchor] = useState();

  const scrollRefsObj = {};

  const getObserverConfig = () => {
    const observerOffset = Math.floor(window.innerHeight / 2);
    const observerConfig = {
      rootMargin: `-${
        window.innerHeight % 2 === 0 ? observerOffset - 1 : observerOffset
      }px 0px -${observerOffset}px 0px`,
    };

    setScrollMargin(observerConfig);
  };

  const handleScroll = (entries) => {
    entries.forEach((entry) => {
      if (entry.target.id !== activeAnchor && entry.isIntersecting) setActiveAnchor(entry.target.id);
    });
  };

  useEffect(() => {
    getObserverConfig();
    const handleResize = () => setTimeout(() => getObserverConfig(), 300);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  sidemenuData.list.map((item) => {
    scrollRefsObj[item.id] = createRef();
    return scrollRefsObj;
  }, {});

  useEffect(() => {
    if (typeof window !== 'undefined' && router.query.download === 'pdf') {
      // Set class and state when viewing Pdf version of a playbook
      document.documentElement.classList.add('PlaybookPdf');
    }
  });

  return (
    <>
      <SeoMeta {...seoData} />
      <PageWrapper>
        <Navigation {...navigationData} />
        {playbookHeaderData && <PlaybookHeader {...playbookHeaderData} />}
        <PlaybookLayout
          asideContent={
            sidemenuData && <SideMenu {...sidemenuData} scrollRefs={scrollRefsObj} activeAnchor={activeAnchor} />
          }
          mainContent={
            <>
              {summaryBlockData && (
                <SummaryBlock
                  {...summaryBlockData}
                  scrollRefs={scrollRefsObj}
                  scrollMargin={scrollMargin}
                  handleScroll={handleScroll}
                  playbookTitle={playbookHeaderData.title}
                />
              )}
              {chaptersBlockData &&
                chaptersBlockData.map((chapter, index) => (
                  <Chapter
                    key={`Chapter-${index}--${data.sys.id}`}
                    {...chapter}
                    scrollRefs={scrollRefsObj}
                    scrollMargin={scrollMargin}
                    handleScroll={handleScroll}
                  />
                ))}
            </>
          }
        />
        {impactBlockData && (
          <ImpactBlock
            {...impactBlockData}
            scrollRefs={scrollRefsObj}
            scrollMargin={scrollMargin}
            handleScroll={handleScroll}
            playbookTitle={playbookHeaderData.title}
          />
        )}
        {subscribeBlockData && <SubscribeBlock {...subscribeBlockData} playbookTitle={playbookHeaderData.title} />}
        {callToActionData && <CallToAction {...callToActionData} source="playbooks-page" />}
        <Footer />
      </PageWrapper>
    </>
  );
};

export async function getStaticPaths() {
  const client = contentfulClient;
  let entries = [];
  await client
    .getEntries({
      content_type: 'playbook',
    })
    .then((data) => {
      entries = data.items;
    });
  // .catch(console.error);

  // Map the slug for the playubook entries
  const paths = entries.map((item) => ({ params: { slug: item.fields.slug } }));

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const client = contentfulClient;
  let entryTemp = {};

  // Fetch data from contentful using created fields
  await client
    .getEntries(
      {
        content_type: 'playbook',
        'fields.slug': params.slug,
        include: 4,
      },
      {
        content_type: 'playbook',
        'fields.slug': params.slug,
        include: 1,
      },
    )
    .then((entry) => {
      entryTemp = entry;
    });

  // .catch(console.error) TODO: bugsnag;

  return {
    props: {
      playbook: JSON.parse(safeJsonStringify(entryTemp)),
    },
  };
}

export default Playbook;
